import { Extension } from '@tiptap/core';
import { PluginKey, TextSelection } from '@tiptap/pm/state';
import { ReactRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import tippy from 'tippy.js';
import ScriptCharacterList from '../ScriptCharacterList';
import {
	SCRIPT_CHARACTER,
	SCRIPT_DIALOGUE,
	charactersFromNames,
	filterCharacters,
} from '../../../services/scriptFormat';

/* Docs — character autocomplete on a script's cue lines.
 *
 * Reuses the @tiptap/suggestion machinery that already drives the "/" menu and
 * the "@" entity chips, so there is one popup pattern in the codebase rather
 * than three.
 *
 * NO TRIGGER CHARACTER. You do not type a sigil to name a speaker — the cue
 * line IS the trigger. Suggestion supports this through its injectable
 * `findSuggestionMatch`: the matcher below reports a match whenever the cursor
 * sits in a scriptCharacter node and nothing anywhere else, which also means an
 * EMPTY cue opens the full cast list. That is the moment the feature earns its
 * keep — Enter after a speech lands you on a blank cue, and the people who
 * might speak next appear without a keystroke.
 *
 * The plugin key MUST be distinct: the slash menu already occupies the default
 * SuggestionPluginKey, and ProseMirror throws on two instances of a keyed
 * plugin. It is exported because the script Enter keymap reads it to know when
 * the picker owns the Return key.
 */

export const ScriptCharacterPluginKey = new PluginKey('scriptCharacterSuggestion');

/** Every character cue in the live document, ranked, without serializing it.
 *
 * The cue being TYPED INTO is excluded. It is a cue node like any other, so a
 * naive scan collects the half-finished name and offers it back — type "M" and
 * the list proposes "M" above MARGO, which is both useless and ahead of the
 * name you were reaching for. A character is someone who appears ELSEWHERE in
 * the script.
 */
export function collectCharacters(editor) {
	const names = [];
	const { $from } = editor.state.selection;
	const skipPos = $from.parent.type.name === SCRIPT_CHARACTER ? $from.before() : null;
	editor.state.doc.descendants((node, pos) => {
		if (node.type.name !== SCRIPT_CHARACTER) return;
		if (skipPos !== null && pos === skipPos) return;
		names.push(node.textContent);
	});
	return charactersFromNames(names);
}

/** Does the picker currently have anything to offer for `query`? */
export function hasCharacterMatches(editor, query) {
	return filterCharacters(collectCharacters(editor), query || '').length > 0;
}

/* Match the cue line the cursor is in — from the start of the node to the
   caret — and nothing else. Returning null everywhere else is what keeps this
   scoped; the plugin runs the matcher on every transaction, so it stays O(1). */
function findCharacterCueMatch({ $position }) {
	if ($position.parent.type.name !== SCRIPT_CHARACTER) return null;
	const from = $position.start();
	return {
		range: { from, to: $position.pos },
		query: $position.parent.textBetween(0, $position.parentOffset, '', ''),
		text: $position.parent.textContent,
	};
}

const ScriptCharacterSuggestion = Extension.create({
	name: 'scriptCharacterSuggestion',

	addProseMirrorPlugins() {
		return [
			Suggestion({
				editor: this.editor,
				pluginKey: ScriptCharacterPluginKey,
				char: '',
				findSuggestionMatch: findCharacterCueMatch,

				items: ({ editor, query }) => filterCharacters(collectCharacters(editor), query),

				/* Choosing a name COMMITS the cue and starts the speech, which is
				   also what makes Enter safe here: filling the name in place would
				   leave the cursor in a cue that still matches, so the picker would
				   reopen and Enter could never advance — you would be trapped on
				   the cue line. One keystroke: name chosen, speech begun.

				   Replaces the whole node rather than the suggestion range, because
				   the caret can sit mid-name (click into "MARGO" after "MAR" and a
				   range-only insert leaves "MARGOGO"). */
				/* Replace the cue with the chosen name, then open the speech.
				 *
				 * Committing the name AND advancing is what makes Return safe here:
				 * filling the name in place leaves the caret in a cue that still
				 * matches, so the picker reopens and Return can never advance — you
				 * are stuck on the cue line. One keystroke: name chosen, speech begun.
				 *
				 * ONE transaction, and deliberately NOT a chain of TipTap commands.
				 * Three shapes failed here, each only visible in a real browser:
				 *   1. insert → splitBlock, trusting where the insert left the caret:
				 *      the split landed at the HEAD of the cue, so the cue itself
				 *      became the speech — "EVE" as dialogue, with no speaker.
				 *   2. absolute positions captured from editor.state before the chain
				 *      mutated the doc: "Position 82 out of range".
				 *   3. tr-derived positions, but still chaining splitBlock/setNode —
				 *      those commands resolve against the ORIGINAL state.selection,
				 *      not the transaction being built, so the split and the retype
				 *      both landed a node off and shredded the surrounding elements.
				 * tr.split with an explicit typesAfter does the whole thing atomically.
				 *
				 * The node is replaced whole rather than the suggestion range, because
				 * the caret can sit mid-name: click into "MARGO" after "MAR" and a
				 * range-only insert leaves "MARGOGO".
				 */
				command: ({ editor, props }) => {
					editor
						.chain()
						.focus()
						.command(({ tr }) => {
							const { $from } = tr.selection;
							const from = $from.start();
							tr.insertText(props.name, from, $from.end());
							const caret = from + props.name.length;
							tr.split(caret, 1, [
								{ type: editor.schema.nodes[SCRIPT_DIALOGUE] },
							]);
							/* Land the caret INSIDE the new speech. A split inserts a
							   closing and an opening token, so the new block's content
							   starts two positions on: caret+1 sits between the blocks
							   and resolves to the document (no cursor anywhere), while
							   `near(caret)` returns caret itself — that position is
							   still valid, at the end of the cue, so the speech got
							   typed into the character's name ("MARGOPlaces, everyone.").
							   near() around caret+2 is explicit about the target and
							   still snaps rather than throwing at the document edge. */
							tr.setSelection(
								TextSelection.near(
									tr.doc.resolve(Math.min(caret + 2, tr.doc.content.size)),
									1
								)
							);
							return true;
						})
						.run();
				},

				render: () => {
					let component;
					let popup;

					/* A popup with nothing in it must not appear: a new character's
					   name matches nobody, and an empty "Nothing matches" box sitting
					   over the cue would both mislead and swallow Return. */
					const setVisible = (props) => {
						if (!popup?.[0]) return;
						if (props.items.length === 0) popup[0].hide();
						else popup[0].show();
					};

					return {
						onStart: (props) => {
							component = new ReactRenderer(ScriptCharacterList, {
								props,
								editor: props.editor,
							});

							if (!props.clientRect) return;

							popup = tippy('body', {
								getReferenceClientRect: props.clientRect,
								appendTo: () => document.body,
								content: component.element,
								showOnCreate: props.items.length > 0,
								interactive: true,
								trigger: 'manual',
								placement: 'bottom-start',
							});
							setVisible(props);
						},

						onUpdate(props) {
							component.updateProps(props);
							if (!props.clientRect) return;
							popup[0].setProps({ getReferenceClientRect: props.clientRect });
							setVisible(props);
						},

						onKeyDown(props) {
							if (props.event.key === 'Escape') {
								popup[0].hide();
								return true;
							}
							return component.ref?.onKeyDown(props);
						},

						onExit() {
							popup[0]?.destroy();
							component?.destroy();
						},
					};
				},
			}),
		];
	},
});

export default ScriptCharacterSuggestion;
